import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { GoalEdit } from '../model/goal';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code } from 'src/app/core/model/dictionary';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { SettingService } from 'src/app/core/service/setting.service';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-goal-edit',
  templateUrl: './goal-edit.component.html',
  styleUrls: ['./goal-edit.component.scss']
})
export class GoalEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public isNew = false;
  @Input() public memberId: number;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<GoalEdit> = new EventEmitter();
  @Output() public delete: EventEmitter<GoalEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public confirmDeleteDialogOpened = false;
  public goalToSave: any;
  public allTypesOfGoal: Code[] = [];
  public allGoalsProgress: Code[] = [];
  
  public goalDictionary = '';
  public allowFreeTextForNewGoals: boolean;
  
  public goals: Code[];
  public allGoals: Code[];

  /**
   * Filtered list of flags for drop-down.
   */  
  public goalsFiltered: Code[] = [];
  public allGoalsFiltered: Code[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for goal virtualization. This is to accelerate the DOM performance.
   */
  public virtualGoal: any = {
    itemHeight: 30
  };  
  
  private goalEdit: GoalEdit;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService,
    private settingService: SettingService,)
  {
  }

  public get goalText(): UntypedFormControl
  {
    return this.editForm.get('goalText');
  }

  public get typesOfGoal(): UntypedFormControl
  {
    return this.editForm.get('typesOfGoal');
  }

  public get progress(): UntypedFormControl
  {
    return this.editForm.get('progress');
  }

  public get goalDictionaryValue(): UntypedFormControl
  {
    return this.editForm.get('goalDictionaryValue');
  }

  @Input() public set model(goalEdit: GoalEdit)
  {
    this.initModel(goalEdit);
  }

  public ngOnInit(): void
  {
    this.dictionaryService.getCachedTypeOfGoal().subscribe({
      next: (data) =>
      {
        this.allTypesOfGoal = data;
      }
    });

    this.dictionaryService.getCachedGoalProgress().subscribe({
      next: (data) =>
      {
        this.allGoalsProgress = data;
      }
    });

    this.dictionaryService.getCachedGoal().subscribe({
      next: (data) =>
      {        
        this.goals = sortBy(data, ['displayName']);
        this.allGoals = this.goals;
        this.filterGoals('');        
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.goalDictionary = sessionSetting.goalDictionary;
        this.allowFreeTextForNewGoals = sessionSetting.allowFreeTextForNewGoals;
      }
    });
  }

  /**
   * Filters the goals based on user input.
   */
  public filterGoals(filter: string): void
  {
    this.searchTerm = filter;
    this.goalsFiltered = this.goals
      .filter(flag => flag.displayName.toLowerCase().includes(filter.toLowerCase()));
  }  

  /**
   * Filters all goals based on user input.
   */
  public filterAllGoals(filter: string): void
  {
    this.searchTerm = filter;
    this.allGoals = this.goals;
    this.allGoalsFiltered = this.allGoals
      .filter(goal => goal.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = this.editForm.value;

    if (this.goalDictionary !== '' && this.isNew && !this.allowFreeTextForNewGoals)
    {
      const goalType = this.allGoals.find(x => x.code === objectToPersist.goalText);
      objectToPersist.goalId = (goalType !== undefined) ? goalType.id : null;
      objectToPersist.goalText = (goalType !== undefined) ? goalType.displayName : null;
    }
    else
    {
      const goalType = this.allGoals.find(x => x.displayName === objectToPersist.goalText);
      objectToPersist.goalId = (goalType !== undefined) ? goalType.id : null;
    }    

    this.save.emit(objectToPersist);
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  public onDelete(e: Event): void
  {
    e.preventDefault();

    this.delete.emit(this.goalEdit);
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    document.body.style.overflow = '';
    this.cancelling.emit();
  }

  private initModel(goalEdit: GoalEdit)
  {
    if (goalEdit === undefined) { return; }

    if (this.isNew)
    {
      // Set form
      this.editForm = this.fb.group({
        id: [null],
        memberId: [null],
        goalText: ['', Validators.required],
        progress: [null, Validators.required],
        typesOfGoal: [null, Validators.required]
      });
    }
    else
    {
      this.editForm = this.fb.group({
        id: [goalEdit.id],
        memberId: [goalEdit.memberId],
        goalText: [{ value: goalEdit.goal, disabled: false }, Validators.required],
        progress: [{ value: goalEdit.progress, disabled: false }, Validators.required],
        typesOfGoal: [{ value: goalEdit.type, disabled: false }, Validators.required]
      });
    }

    this.goalEdit = goalEdit;

    this.editFormActive = true;

    if (this.memberId !== undefined)
    {
      this.editForm.controls.memberId.setValue(this.memberId);
    }
    this.allGoalsFiltered = this.goalsFiltered;
  }
}
